import { useState, type ReactNode } from 'react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent, cn } from '@design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { InsightHighlightedTitle } from '@/components/InsightHighlightedTitle';

interface CostFactorInsightCardProps {
  insight: ClientInsight;
  icon: ReactNode;
  cost: number;
  changeType: 'increase' | 'decrease';
  expandedContent?: ReactNode;
}

export function CostFactorInsightCard({
  insight,
  icon,
  cost,
  changeType,
  expandedContent,
}: CostFactorInsightCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <CollapsibleTrigger className="flex w-full cursor-pointer items-start gap-3 p-5">
          <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
            {icon}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              {insight.categoryLabel}
            </p>
            <p className="mt-1 text-sm font-semibold leading-snug text-gray-950">
              <InsightHighlightedTitle title={insight.title} highlights={insight.titleHighlights} />
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
              {insight.description}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn('whitespace-nowrap text-base font-semibold', {
                'text-red-700': changeType === 'increase',
                'text-green-700': changeType === 'decrease',
              })}
            >
              {changeType === 'decrease' ? '-' : '+'}
              <LtcCostDisplay value={cost} />
            </span>
            {open ? (
              <ChevronUp className="text-muted-foreground size-4" />
            ) : (
              <ChevronDown className="text-muted-foreground size-4" />
            )}
          </div>
        </CollapsibleTrigger>
        {expandedContent && (
          <CollapsibleContent>
            <div className="border-t border-gray-100 px-5 pb-5 pt-4">
              {expandedContent}
            </div>
          </CollapsibleContent>
        )}
      </div>
    </Collapsible>
  );
}
